/**
 * @file QaSessionLab.js
 * @brief Developer-tools page that provisions a disposable Q&A session (plan §3.2).
 *
 * The control plane for the Q&A console: create a fully-provisioned session, hand back a
 * production-shaped launch link, and tear it all down again. Desktop-first at one width —
 * this is an internal English-only tool, so §3.3's responsive matrix does not apply.
 *
 * Five states, all reachable from here: idle · creating · partial-error · ready · cleanup-error.
 *
 * Scope is LOCAL by default. The seed and register-host seams are AuthorizationLevel.Function
 * and localhost ignores function keys; staging stays disabled until an operator supplies a key,
 * which is held in component memory only — never storage, URLs, logs or telemetry. Production
 * is not an option at all.
 */

import { useMemo, useState } from 'react';
import { useQaSessionLab } from '../hooks/useQaSessionLab';
import './QaSessionLab.css';

/** Production is deliberately absent. */
const LAB_ENVIRONMENTS = [
  { id: 'local', label: 'Local', url: 'http://localhost:7071/api', consoleOrigin: 'http://localhost:3001' },
  {
    id: 'staging',
    label: 'Staging',
    url: 'https://realtranslation-func-dev-eastus2.azurewebsites.net/api',
    consoleOrigin: 'https://app-test.qolsync.ai'
  }
];

const TIERS = ['Pro', 'WebinarPro', 'WebinarStarter', 'Starter'];

export default function QaSessionLab() {
  const [environmentId, setEnvironmentId] = useState('local');
  const [tier, setTier] = useState('Pro');
  const [passwordRevealed, setPasswordRevealed] = useState(false);

  const environment = useMemo(
    () => LAB_ENVIRONMENTS.find((e) => e.id === environmentId) ?? LAB_ENVIRONMENTS[0],
    [environmentId]
  );

  const lab = useQaSessionLab({
    functionsUrl: environment.url,
    consoleOrigin: environment.consoleOrigin
  });

  const busy = lab.state === 'creating' || lab.state === 'cleaning';

  const handleEnvironmentChange = (id) => {
    setEnvironmentId(id);
    setPasswordRevealed(false);
  };

  return (
    <div className="qa-lab">
      <header className="qa-lab__header">
        <h1>QA Session Lab</h1>
        <p className="qa-lab__subtitle">
          Provision a disposable Q&amp;A session and open the moderator console against it.
        </p>
      </header>

      <section className="qa-lab__panel" aria-labelledby="qa-lab-setup">
        <h2 id="qa-lab-setup">Setup</h2>

        <div className="qa-lab__field">
          <span className="qa-lab__label" id="qa-lab-env-label">Environment</span>
          <div className="qa-lab__pills" role="group" aria-labelledby="qa-lab-env-label">
            {LAB_ENVIRONMENTS.map((env) => (
              <button
                key={env.id}
                type="button"
                className={`qa-lab__pill${environmentId === env.id ? ' qa-lab__pill--active' : ''}`}
                aria-pressed={environmentId === env.id}
                onClick={() => handleEnvironmentChange(env.id)}
                disabled={busy}
              >
                {env.label}
              </button>
            ))}
          </div>
        </div>

        <div className="qa-lab__field">
          <label className="qa-lab__label" htmlFor="qa-lab-tier">Plan tier</label>
          <select
            id="qa-lab-tier"
            className="qa-lab__input"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
            disabled={busy}
          >
            {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <p className="qa-lab__hint">
            Seeded as <code>productType=webinar</code>: meeting plans carry no Q&amp;A features.
          </p>
        </div>

        <button
          type="button"
          className="qa-lab__primary"
          onClick={() => lab.createSession({ tier })}
          disabled={busy}
        >
          {lab.state === 'creating' ? 'Creating…' : 'Create test session'}
        </button>
      </section>

      {/* Progress and failures are announced, not just coloured. */}
      <div className="qa-lab__status" role="status" aria-live="polite">
        {lab.state === 'creating' && <p>Provisioning: account → tenant → subscription → session…</p>}
        {lab.state === 'cleaning' && <p>Tearing down…</p>}
      </div>

      {lab.state === 'partial-error' && (
        <section className="qa-lab__panel qa-lab__panel--error" role="alert">
          <h2>Setup stopped at: {lab.failedStep}</h2>
          <p>{lab.error}</p>
          <p className="qa-lab__hint">
            Retry resumes at this step with the same payload — it never re-registers the account,
            and a changed payload would be refused by the owned-session contract.
          </p>
          <button type="button" className="qa-lab__primary" onClick={lab.retry} disabled={busy}>
            Retry from {lab.failedStep}
          </button>
        </section>
      )}

      {lab.state === 'cleanup-error' && (
        <section className="qa-lab__panel qa-lab__panel--error" role="alert">
          <h2>Cleanup failed at: {lab.failedStep}</h2>
          <p>{lab.error}</p>
          <p className="qa-lab__hint">
            Nothing is stranded permanently: the session row expires on its 60-minute idle Redis
            TTL, questions on the 900-second Cosmos TTL, and the intentionally retained Q&amp;A
            cache keys within 7 days.
          </p>
          <button type="button" className="qa-lab__primary" onClick={lab.cleanup}>
            Retry cleanup
          </button>
        </section>
      )}

      {lab.session && (
        <section className="qa-lab__panel" aria-labelledby="qa-lab-ready">
          <h2 id="qa-lab-ready">Session ready</h2>

          <dl className="qa-lab__facts">
            <dt>Session id</dt>
            <dd><code>{lab.session.sessionId}</code></dd>
            <dt>Tenant</dt>
            <dd><code>{lab.session.tenantId}</code></dd>
            <dt>Run id</dt>
            <dd><code>{lab.session.testRunId}</code></dd>
            <dt>Subscription TTL</dt>
            <dd>{lab.session.subscriptionTtl ?? 'unknown'}s</dd>
          </dl>

          <div className="qa-lab__field">
            <span className="qa-lab__label">Sign-in credentials</span>
            <p className="qa-lab__hint">
              Sign in at the console&apos;s own origin — no token is handed over, and the two
              origins share no storage.
            </p>
            <p><code>{lab.credentials.email}</code></p>
            <div className="qa-lab__inline">
              <input
                type={passwordRevealed ? 'text' : 'password'}
                className="qa-lab__input"
                value={lab.credentials.password ?? ''}
                readOnly
                aria-label="Generated password"
              />
              <button
                type="button"
                className="qa-lab__secondary"
                onClick={() => setPasswordRevealed((v) => !v)}
                aria-pressed={passwordRevealed}
              >
                {passwordRevealed ? 'Hide' : 'Reveal'}
              </button>
              <button
                type="button"
                className="qa-lab__secondary"
                onClick={() => navigator.clipboard?.writeText(lab.credentials.password ?? '')}
              >
                Copy
              </button>
            </div>
          </div>

          <div className="qa-lab__field">
            <span className="qa-lab__label">Launch links</span>
            <p>
              <a href={lab.launchUrl} target="_blank" rel="noreferrer">Moderator console</a>
              <span className="qa-lab__hint">
                {' '}— carries only <code>intent=moderate</code>. It grants nothing: it opens the
                LOGIN page when this browser has no moderator session for the room, and moderator
                itself comes from <code>/api/qa/connect</code> after you sign in.
              </span>
            </p>
            <p>
              <a href={lab.participantUrl} target="_blank" rel="noreferrer">Participant view</a>
              <span className="qa-lab__hint"> — open in a separate clean browser context.</span>
            </p>
          </div>

          <button type="button" className="qa-lab__secondary" onClick={lab.cleanup} disabled={busy}>
            Tear down session
          </button>
        </section>
      )}
    </div>
  );
}
